import { createContext, useEffect, useReducer } from 'react';
import { apiHost } from '../config';
import PropTypes from 'prop-types';
import firebase from '../lib/firebase';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'AUTH_STATE_CHANGED') {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  platform: 'Firebase',
  createUserWithEmailAndPassword: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((user) => {
        if (user) {
          // Here you should extract the complete user profile to make it available in your entire app.
          // The auth state only provides basic information.
          dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }),
    [dispatch]
  );

  const signInWithEmailAndPassword = async (email, password) => {
    const response = await firebase
      .auth()
      .signInWithEmailAndPassword(email, password);
    return response.user;
  };

  const signInWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();

    return firebase.auth().signInWithPopup(provider);
  };

  const createUserWithEmailAndPassword = async (email, password, trainerId) => {
    if (!trainerId) {
      throw new Error('No trainerId.');
    }
    const response = await firebase
      .auth()
      .createUserWithEmailAndPassword(email, password);
    const token = await response.user?.getIdToken();

    const url = `${apiHost}/v1/accounts/complete_signup`;

    const completeSignupResponse = await fetch(url, {
      method: 'POST',
      withCredentials: true,
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fromTrainerId: trainerId }),
    });

    if (completeSignupResponse.status !== 201) {
      const msg = await response.text;
      throw new Error(msg);
    }
    return { user: response.user, success: true };
  };

  const logout = async () => {
    await firebase.auth().signOut();
  };

  const resetPassword = async (resetToken, password) => {
    if (!resetToken) {
      throw new Error('No token given.');
    }
    if (!password) {
      throw new Error('No password given.');
    }

    const url = `${apiHost}/v1/accounts/reset_password`;

    const response = await fetch(url, {
      method: 'POST',
      withCredentials: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ resetToken, password }),
    });

    const json = await response.json();

    if (response.status !== 200) {
      throw new Error(json.message);
    }
    return { redirectUrl: json.redirectUrl, success: true };
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'Firebase',
        createUserWithEmailAndPassword,
        signInWithEmailAndPassword,
        signInWithGoogle,
        logout,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
